/**
 * Reward Claim Composable — 帶 receiveNo 的領獎唯一出口
 *
 * `activityApi.getReward`、`gameApi.getFreeGameReward` 與 `applyType: 'activity'` 的 `paymentApi.getWithdrawRulePreview`
 * 只能從這裡送出（eslint.config.mjs 強制）。
 * 送出前以 evaluateClaim() 判定該 receiveNo 是否屬於目前已確認的身分；證明不了就不送（fail-closed）。
 * 規格見 spec/2026-10-19-reward-claim-identity-scope/requirements.md §4.3。
 */

import { storeToRefs } from 'pinia'

import { activityApi, gameApi, paymentApi } from '@/api'
import { useAuthStore } from '@/stores/auth/auth'
import { CLAIM_BLOCK_REASON, evaluateClaim, toClaimReceiveNo } from '@/utils/identityGuard'

// 這兩種代表「畫面上出現了不該出現的 receiveNo」，是要追的異常；轉換期與未確認屬於正常的短暫狀態，不上報
const REPORTED_BLOCK_REASONS = [CLAIM_BLOCK_REASON.NOT_ADMITTED, CLAIM_BLOCK_REASON.FOREIGN]

export const useRewardClaim = () => {
  const nuxtApp = useNuxtApp()
  const { $request, $i18n } = nuxtApp
  const { appSecret } = useRuntimeConfig().public
  const { isLoggedIn } = useAuthToken()
  const { isAutoLoggingIn, isRevivingSession, isBackgroundEpisode } = storeToRefs(useAuthStore())
  const identity = useIdentityScope()
  const { fetchUserInfo } = useUserInfo()

  const onBlocked = ({ kind, receiveNo, reason }) => {
    if (reason === CLAIM_BLOCK_REASON.UNCONFIRMED) {
      // 讓下一次點擊有答案；失敗等輪詢重試，錯誤已於 request 層上報
      fetchUserInfo().catch(() => {})
    }

    if (REPORTED_BLOCK_REASONS.includes(reason)) {
      nuxtApp.$sentry?.captureMessage('[reward] Claim blocked by identity scope', {
        level: 'warning',
        tags: { reason, kind },
        extra: { receiveNo },
      })
    }

    // $i18n 在 plugin 階段可能尚未就緒，故防護
    const blockedMessage = $i18n?.t('description.please_try_again_later')
    if (blockedMessage) nuxtApp.$toast?.error(blockedMessage)

    const error = new Error('REWARD_CLAIM_BLOCKED')
    error.code = 'REWARD_CLAIM_BLOCKED'
    error.reason = reason
    // 沿用 request 層「已呈現、勿重複上報」的契約：呼叫端據此吞掉 rejection
    error.isBusinessError = true
    return error
  }

  const authorize = ({ kind, receiveNo }) => {
    const source = identity.sourceOf(receiveNo)
    const verdict = evaluateClaim({
      receiveNo,
      isLoggedIn: isLoggedIn.value,
      isTransitioning: isAutoLoggingIn.value || isRevivingSession.value || isBackgroundEpisode.value,
      currentOwner: identity.currentOwner(),
      sourceOwner: source.owner,
      isAdmitted: source.isAdmitted,
    })
    if (!verdict.allowed) {
      throw onBlocked({ kind, receiveNo, reason: verdict.reason })
    }
  }

  // 只帶 activityNo（如新手獎勵）時不做身分判定：後端以 token 決定領誰的（REQ-14）
  const claimReward = async ({ activityNo, receiveNo, attended }) => {
    authorize({ kind: 'reward', receiveNo })
    return $request.send(activityApi.getReward({ activityNo, receiveNo, appSecret, attended }))
  }

  const claimFreeGame = async ({ activityNo, receiveNo }) => {
    authorize({ kind: 'freeGame', receiveNo })
    return $request.send(gameApi.getFreeGameReward({ activityNo, receiveNo }))
  }

  // 預覽以 applyNo 帶著清單上的 receiveNo 送出（免費遊戲的預覽還會在後端配發新的 receiveNo），
  // 所以在領獎之前就要判定；請求內容維持原值，只有判定用正規化後的值（BR-7）
  const previewReward = async ({ receiveNo }) => {
    authorize({ kind: 'rewardPreview', receiveNo: toClaimReceiveNo(receiveNo) })
    return $request.send(paymentApi.getWithdrawRulePreview({ applyType: 'activity', applyNo: receiveNo, amount: null }))
  }

  const previewFreeGame = async ({ receiveNo }) => {
    authorize({ kind: 'freeGamePreview', receiveNo: toClaimReceiveNo(receiveNo) })
    return $request.send(paymentApi.getWithdrawRulePreview({ applyType: 'activity', applyNo: receiveNo, amount: null, isFreeGame: true }))
  }

  return {
    claimReward,
    claimFreeGame,
    previewReward,
    previewFreeGame,
  }
}
